import CircularProgress from "@material-ui/core/CircularProgress";
import Slide from "@material-ui/core/Slide";
import { Dialog, DialogActions, DialogContent } from "@material-ui/core";
import { forwardRef, useEffect, useState } from "react";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";
import FileUploader from "./FileUploader";

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById("upload-form-font")) return;
	const link = document.createElement("link");
	link.id = "upload-form-font";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap";
	document.head.appendChild(link);
};

// ─── Inline styles ─────────────────────────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById("upload-form-styles")) return;
	const style = document.createElement("style");
	style.id = "upload-form-styles";
	style.textContent = `
		.uf-wrap, .uf-wrap *, .uf-wrap *::before, .uf-wrap *::after {
			box-sizing: border-box;
		}
		.uf-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			width: 100%;
		}

		/* ── Split card ── */
		.uf-card {
			width: 100%;
			display: grid;
			grid-template-columns: 200px 1fr;
			background: #fff;
			border-radius: 0 0 20px 20px;
			border: 1px solid rgba(0,0,0,0.06);
			border-top: none;
			box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 20px 48px -20px rgba(0,0,0,0.14);
			overflow: hidden;
		}

		/* ── Left orange panel ── */
		.uf-card__left {
			background: linear-gradient(155deg, #F4A96A 0%, #E47029 50%, #B94D1A 100%);
			padding: 32px 22px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: relative;
			overflow: hidden;
		}
		.uf-card__left::before {
			content: '';
			position: absolute;
			width: 200px; height: 200px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			right: -80px; top: -80px;
		}
		.uf-card__left::after {
			content: '';
			position: absolute;
			width: 140px; height: 140px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.12);
			left: -50px; bottom: -50px;
		}
		.uf-card__left-top { position: relative; z-index: 1; }
		.uf-card__left-icon {
			width: 44px; height: 44px;
			border-radius: 12px;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 16px;
		}
		.uf-card__left-title {
			font-family: 'Manrope', sans-serif;
			font-size: 16px;
			font-weight: 800;
			color: #fff;
			letter-spacing: -0.01em;
			line-height: 1.3;
			margin-bottom: 10px;
		}
		.uf-card__left-sub {
			font-size: 12px;
			color: rgba(255,255,255,0.78);
			line-height: 1.55;
		}
		.uf-card__left-bottom { position: relative; z-index: 1; }
		.uf-card__left-steps {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}
		.uf-card__left-step {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 11.5px;
			color: rgba(255,255,255,0.65);
		}
		.uf-card__left-step.active {
			color: #fff;
			font-weight: 600;
		}
		.uf-step-num {
			width: 18px; height: 18px;
			border-radius: 50%;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 9px;
			font-weight: 700;
			flex-shrink: 0;
		}
		.uf-card__left-step.active .uf-step-num {
			background: #fff;
			color: #E47029;
		}

		/* ── Right form panel ── */
		.uf-card__right { padding: 32px 28px; }
		.uf-card__right-label {
			font-size: 11px;
			font-weight: 700;
			color: #E47029;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			margin-bottom: 8px;
		}
		.uf-card__right-title {
			font-family: 'Manrope', sans-serif;
			font-size: 18px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.01em;
			margin-bottom: 22px;
		}

		/* fields */
		.uf-field { margin-bottom: 16px; }
		.uf-field label {
			display: block;
			font-size: 11.5px;
			font-weight: 600;
			color: #5B5B60;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			margin-bottom: 7px;
		}
		.uf-field input {
			width: 100%;
			height: 46px;
			padding: 0 14px;
			border: 1.5px solid rgba(0,0,0,0.11);
			border-radius: 10px;
			font-size: 14px;
			font-family: inherit;
			color: #18181A;
			background: #FAFAFA;
			-webkit-appearance: none;
			appearance: none;
			transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
		}
		.uf-field input:hover { border-color: #c2bdb3; }
		.uf-field input:focus {
			outline: none;
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
			background: #fff;
		}
		.uf-field input:-webkit-autofill,
		.uf-field input:-webkit-autofill:hover,
		.uf-field input:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px #FAFAFA inset;
			-webkit-text-fill-color: #18181A;
		}
		.uf-field input.error { border-color: #E53E3E; }
		.uf-field input.error:focus { box-shadow: 0 0 0 3px rgba(229,62,62,0.12); }
		.uf-field__hint { font-size: 11px; color: #C0BDB8; margin-top: 4px; }
		.uf-field__error { font-size: 11px; color: #E53E3E; margin-top: 4px; }

		/* button */
		.uf-btn {
			width: 100%;
			height: 48px;
			border: none;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			font-family: 'Manrope', sans-serif;
			font-size: 14.5px;
			font-weight: 700;
			cursor: pointer;
			margin-top: 6px;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			transition: background 0.15s, transform 0.1s;
			letter-spacing: 0.01em;
		}
		.uf-btn:hover:not(:disabled) { background: #B94D1A; }
		.uf-btn:active:not(:disabled) { transform: scale(0.99); }
		.uf-btn:disabled { background: #E5C9B0; cursor: not-allowed; }

		.uf-spinner-wrap {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 48px;
			margin-top: 6px;
		}
		.uf-security {
			display: flex;
			align-items: center;
			gap: 6px;
			margin-top: 12px;
			font-size: 11px;
			color: #C0BDB8;
			justify-content: center;
		}

		/* ── Error dialog ── */
		.uf-dialog-head {
			padding: 20px 22px;
			background: radial-gradient(73.95% 73.95% at 14.58% 11.46%, #EE8F37 0%, #E47029 100%);
			display: flex;
			align-items: center;
			gap: 12px;
			position: relative;
		}
		.uf-dialog-icon {
			width: 34px; height: 34px;
			border-radius: 50%;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
		.uf-dialog-title {
			font-family: 'Manrope', sans-serif;
			font-size: 17px;
			font-weight: 800;
			color: #fff;
			margin: 0;
		}
		.uf-dialog-close {
			position: absolute;
			right: 12px; top: 12px;
			width: 28px; height: 28px;
			border-radius: 50%;
			background: rgba(255,255,255,0.18);
			border: none;
			cursor: pointer;
			color: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
		}
		.uf-dialog-body {
			padding: 20px 22px 8px;
			font-size: 14px;
			color: #5B5B60;
			line-height: 1.55;
		}
		.uf-dialog-actions {
			padding: 10px 22px 20px !important;
			display: flex !important;
			justify-content: flex-end !important;
		}
		.uf-dialog-btn {
			padding: 9px 24px;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			border: none;
			font-size: 13.5px;
			font-weight: 700;
			font-family: inherit;
			cursor: pointer;
			transition: background 0.15s;
		}
		.uf-dialog-btn:hover { background: #B94D1A; }

		/* ── Responsive ── */
		@media (max-width: 520px) {
			.uf-card { grid-template-columns: 1fr; }
			.uf-card__left { padding: 22px; min-height: auto; }
			.uf-card__left-steps { flex-direction: row; gap: 14px; }
			.uf-card__right { padding: 24px 22px; }
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const UploadIcon = ({ color = "currentColor", size = 22 }) => (
	<svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" width={size} height={size} aria-hidden="true">
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
		<polyline points="17 8 12 3 7 8"/>
		<line x1="12" y1="3" x2="12" y2="15"/>
	</svg>
);

const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" aria-hidden="true">
		<rect x="4" y="10" width="16" height="10" rx="2"/>
		<path d="M8 10V7a4 4 0 0 1 8 0v3"/>
	</svg>
);

const AlertIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" width="16" height="16" aria-hidden="true">
		<circle cx="12" cy="12" r="10"/>
		<line x1="12" y1="8" x2="12" y2="12"/>
		<line x1="12" y1="16" x2="12.01" y2="16"/>
	</svg>
);

const CloseIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13" aria-hidden="true">
		<line x1="18" y1="6" x2="6" y2="18"/>
		<line x1="6" y1="6" x2="18" y2="18"/>
	</svg>
);

// ─── Transition ────────────────────────────────────────────────────────────────
const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Main component ────────────────────────────────────────────────────────────
const UploadPatientReportForm = ({
	orgId,
	orgName,
	hashedId,
	sessionId,
	logo,
	userId,
}) => {
	const [loading, setLoading] = useState(false);
	const [isValidUser, setIsValidUser] = useState(false);
	const [displayInvalidDialog, setDisplayInvalidDialog] = useState(false);
	const [values, setValues] = useState({ hin: "", dob: "" });
	const [error, setError] = useState({ hin: false, dob: false });

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const disableButton = () => error.hin || error.dob;

	const handleSubmit = async () => {
		setLoading(true);
		try {
			const validate = await api(
				apiConfigs.baseOption("post", "plugin/verifyHash", {
					data: { orgName, hashedId, sessionId, hin: values.hin, dob: values.dob },
				})
			);
			if (validate.status === 200) {
				setIsValidUser(true);
			} else {
				throw new Error("unexpected response from server");
			}
		} catch {
			setIsValidUser(false);
			setDisplayInvalidDialog(true);
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (e) => {
		const { id, value } = e.target;
		if (id === "hin") {
			if (/^\d*$/.test(value) && value.length <= 10) {
				setValues((prev) => ({ ...prev, hin: value }));
			}
		} else if (id === "dob") {
			let v = value.replace(/[^0-9]/g, "");
			if (v.length > 4) v = v.slice(0, 4) + "-" + v.slice(4);
			if (v.length > 7) v = v.slice(0, 7) + "-" + v.slice(7, 9);
			setValues((prev) => ({ ...prev, dob: v }));
		}
	};

	if (isValidUser) {
		return (
			<FileUploader
				orgId={orgId}
				orgName={orgName}
				hashedId={hashedId}
				sessionId={sessionId}
				hin={values.hin}
				userId={userId}
			/>
		);
	}

	return (
		<div className="uf-wrap">
			<div className="uf-card">

				{/* ── Left orange panel ── */}
				<div className="uf-card__left">
					<div className="uf-card__left-top">
						<div className="uf-card__left-icon">
							<UploadIcon color="#fff" size={22} />
						</div>
						<div className="uf-card__left-title">Upload Your Reports</div>
						<div className="uf-card__left-sub">
							Securely share your medical files with your clinic.
						</div>
					</div>

					<div className="uf-card__left-bottom">
						<div className="uf-card__left-steps">
							<div className="uf-card__left-step active">
								<div className="uf-step-num">1</div>
								Verify identity
							</div>
							<div className="uf-card__left-step">
								<div className="uf-step-num">2</div>
								Upload files
							</div>
							<div className="uf-card__left-step">
								<div className="uf-step-num">3</div>
								Done
							</div>
						</div>
					</div>
				</div>

				{/* ── Right form panel ── */}
				<div className="uf-card__right">
					<div className="uf-card__right-label">Step 1 of 3</div>
					<div className="uf-card__right-title">Verify your identity</div>

					<div className="uf-field">
						<label htmlFor="hin">Health Insurance Number</label>
						<input
							id="hin"
							type="text"
							placeholder="1234567890"
							value={values.hin}
							onChange={handleChange}
							onBlur={() =>
								setError((prev) => ({
									...prev,
									hin: !/^\d{10}$/.test(values.hin),
								}))
							}
							className={error.hin ? "error" : ""}
							maxLength={10}
						/>
						{error.hin
							? <div className="uf-field__error">Must be exactly 10 digits</div>
							: <div className="uf-field__hint">10-digit number on your health card</div>
						}
					</div>

					<div className="uf-field">
						<label htmlFor="dob">Date of Birth</label>
						<input
							id="dob"
							type="text"
							placeholder="YYYY-MM-DD"
							value={values.dob}
							onChange={handleChange}
							onBlur={() =>
								setError((prev) => ({
									...prev,
									dob: !/^\d{4}-\d{2}-\d{2}$/.test(values.dob),
								}))
							}
							className={error.dob ? "error" : ""}
						/>
						{error.dob
							? <div className="uf-field__error">Format must be YYYY-MM-DD</div>
							: <div className="uf-field__hint">e.g. 1990-01-15</div>
						}
					</div>

					{loading ? (
						<div className="uf-spinner-wrap">
							<CircularProgress style={{ color: "#E47029" }} size={28} />
						</div>
					) : (
						<button
							className="uf-btn"
							disabled={disableButton()}
							onClick={handleSubmit}
						>
							<UploadIcon color="#fff" size={15} />
							Upload Report
						</button>
					)}

					<div className="uf-security">
						<LockIcon />
						256-bit encrypted · PHIPA compliant
					</div>
				</div>

			</div>

			{/* ── Invalid login dialog — same logic as original ── */}
			<Dialog
				open={displayInvalidDialog}
				onClose={() => setDisplayInvalidDialog(false)}
				maxWidth="sm"
				fullWidth
				TransitionComponent={Transition}
				aria-labelledby="invalid-login-title"
				aria-describedby="invalid-login-desc"
				PaperProps={{ style: { borderRadius: 18, overflow: "hidden" } }}
			>
				<div className="uf-dialog-head">
					<div className="uf-dialog-icon">
						<AlertIcon />
					</div>
					<h2 id="invalid-login-title" className="uf-dialog-title">
						Invalid login attempt
					</h2>
					<button
						className="uf-dialog-close"
						aria-label="Close dialog"
						onClick={() => setDisplayInvalidDialog(false)}
					>
						<CloseIcon />
					</button>
				</div>

				<DialogContent style={{ padding: "20px 22px 8px" }}>
					<p id="invalid-login-desc" className="uf-dialog-body" style={{ padding: 0 }}>
						Please check the HIN and DOB, then try again.
					</p>
				</DialogContent>

				<DialogActions className="uf-dialog-actions">
					<button
						className="uf-dialog-btn"
						onClick={() => setDisplayInvalidDialog(false)}
						autoFocus
					>
						Close
					</button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default UploadPatientReportForm;